/**
 * Memory presentation (prompt.md §8, §29, §70).
 *
 * WHY promotion is a button and not a policy: a private memory becomes shared
 * *only* through an explicit user action, and the agent has no path to do it. So
 * the promote control appears on private items and nowhere else, and the shared
 * list never offers one — there is nothing to promote from.
 */

import type { MemoryRecord } from "@dayvise/domain";
import { useState } from "react";
import { TextArea } from "../primitives/Fields.tsx";

import { Badge, EmptyState } from "../primitives/Feedback.tsx";
import { Button } from "../primitives/Button.tsx";
import { Tabs } from "../primitives/Navigation.tsx";
import { Card } from "../primitives/Surfaces.tsx";
import { MUTED_TEXT, rowStyle, stackStyle } from "../styles.ts";
import { formatRelativeTime } from "../logic/format.ts";

export interface MemoryListProps {
  readonly privateMemories: readonly MemoryRecord[];
  readonly sharedMemories: readonly MemoryRecord[];
  readonly activeTab?: "private" | "shared";
  readonly onTabChange?: (tab: "private" | "shared") => void;
  readonly onPromote?: (memory: MemoryRecord) => void | Promise<void>;
  readonly onConfirm?: (memory: MemoryRecord) => void | Promise<void>;
  readonly onDelete?: (memory: MemoryRecord) => void | Promise<void>;
  readonly busy?: boolean;
}

/**
 * Private and shared memory, as two tabs rather than one blended list: the
 * distinction *is* the privacy model, and a user must be able to see at a glance
 * what their teammates can read.
 */
export function MemoryList({ privateMemories, sharedMemories, activeTab, onTabChange, onPromote, onConfirm, onDelete, busy = false }: MemoryListProps) {
  const [localTab, setLocalTab] = useState<"private" | "shared">("private");
  const selectedTab = activeTab ?? localTab;
  const list = selectedTab === "private" ? privateMemories : sharedMemories;

  return (
    <div style={stackStyle("var(--dayvise-space-md)")}>
      <Tabs
        label="Memory scope"
        activeId={selectedTab}
        onChange={(id) => { const tab = id === "shared" ? "shared" : "private"; setLocalTab(tab); onTabChange?.(tab); }}
        items={[
          { id: "private", label: `Private (${privateMemories.length})` },
          { id: "shared", label: `Shared with workspace (${sharedMemories.length})` },
        ]}
      >
        <MemoryScopeBody
          scope={selectedTab}
          list={list}
          onPromote={onPromote}
          onConfirm={onConfirm}
          onDelete={onDelete}
          busy={busy}
        />
      </Tabs>
    </div>
  );
}

interface MemoryScopeBodyProps {
  readonly scope: "private" | "shared";
  readonly list: readonly MemoryRecord[];
  readonly onPromote?: (memory: MemoryRecord) => void | Promise<void>;
  readonly onConfirm?: (memory: MemoryRecord) => void | Promise<void>;
  readonly onDelete?: (memory: MemoryRecord) => void | Promise<void>;
  readonly busy: boolean;
}

function MemoryScopeBody({ scope, list, onPromote, onConfirm, onDelete, busy }: MemoryScopeBodyProps) {
  return (
    <div style={stackStyle("var(--dayvise-space-md)")}>
      <p style={{ ...MUTED_TEXT, margin: 0 }}>
        {scope === "private"
          ? "Only you can read these. Admins cannot read them either."
          : "Everyone in this workspace can read these. An agent can only add to them when you share an item yourself."}
      </p>

      {list.length === 0 ? (
        <EmptyState
          title={scope === "private" ? "No private memories" : "Nothing shared yet"}
          description={
            scope === "private"
              ? "The agent records useful facts here as you work."
              : "Use the Share with workspace button on a private memory to make it visible to your team."
          }
        />
      ) : (
        <div style={stackStyle("var(--dayvise-space-sm)")}>
          {list.map((memory) => (
            <MemoryCard key={`${memory.memoryId}:${memory.version}`} memory={memory} onPromote={onPromote} onConfirm={onConfirm} onDelete={onDelete} busy={busy} />
          ))}
        </div>
      )}
    </div>
  );
}

export interface MemoryCardProps {
  readonly memory: MemoryRecord;
  readonly onPromote?: (memory: MemoryRecord) => void | Promise<void>;
  readonly onConfirm?: (memory: MemoryRecord) => void | Promise<void>;
  readonly onDelete?: (memory: MemoryRecord) => void | Promise<void>;
  readonly busy?: boolean;
}

function MemoryCard({ memory, onPromote, onConfirm, onDelete, busy }: MemoryCardProps) {
  const isPrivate = memory.scope === "private";
  const pending = isPrivate && !memory.userConfirmed;
  const [proposal, setProposal] = useState(memory.content);
  return (
    <Card
      title={memory.content.length > 80 ? `${memory.content.slice(0, 79)}…` : memory.content}
      subtitle={`Updated ${formatRelativeTime(memory.updatedAt)}`}
      footer={
        <div style={rowStyle("var(--dayvise-space-xs)")}>
          {pending && onConfirm ? <Button size="sm" disabled={busy || proposal.trim().length === 0} onClick={() => void onConfirm({ ...memory, content: proposal })}>Accept privately</Button> : null}
          {isPrivate && onPromote ? (
            <Button
              size="sm"
              variant="secondary"
              loading={busy}
              onClick={() => void onPromote(memory)}
              title="Makes this memory readable by everyone in the workspace"
            >
              Share with workspace
            </Button>
          ) : null}
          {onDelete ? (
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => void onDelete(memory)}>
              Delete
            </Button>
          ) : null}
        </div>
      }
    >
      <div style={stackStyle("var(--dayvise-space-xs)")}>
        {pending ? <><p style={MUTED_TEXT}>Pending your review. Future runs cannot use this memory until you accept it.</p><TextArea label="Proposed memory" value={proposal} disabled={busy} rows={4} onChange={(event) => setProposal(event.target.value)} /></> : null}
        {!pending ? <p style={{ margin: 0, whiteSpace: "pre-wrap" }}>{memory.content}</p> : null}
        <div style={rowStyle("var(--dayvise-space-xs)", "flex-start")}>
          <Badge tone={isPrivate ? "neutral" : "accent"}>{isPrivate ? "Private" : "Shared"}</Badge>
          <Badge tone={memory.userConfirmed ? "success" : "neutral"}>{memory.userConfirmed ? "Confirmed by you" : "Suggested"}</Badge>
          <Badge tone={memory.sensitivity === "high" ? "danger" : memory.sensitivity === "medium" ? "warning" : "neutral"}>
            {memory.sensitivity} sensitivity
          </Badge>
        </div>
        {memory.promotedAt ? (
          <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-xs)" }}>
            Shared {formatRelativeTime(memory.promotedAt)} — this was your explicit action
          </span>
        ) : null}
      </div>
    </Card>
  );
}
